import {
  isDigitalWellBeingUrl,
  isDigitalWellBeingPath,
  isK5Url,
  isK5Dashboard,
  isMiddleSchoolUrl,
  isMiddleSchoolDashboard,
  isHighSchoolUrl,
} from 'utils/routing/routeHelpers'
import { isHighSchoolDashboard } from 'utils/highschoolHelpers'
import { HIGH_SCHOOL_PROGRAM_TYPE } from 'utils/program/types'
import {
  HS_TRAINING_COURSE_CODE,
  resolveTrainingProgramConfig,
} from 'utils/program/training'
import { resolveCanonicalCourseCode } from 'store/curriculumCourseManager'

import {
  ACCESS_CODES,
  getUserProgramPreferencesFromArray,
} from 'utils/sitePreferenceHelpers'
import { findUserContextSiteById } from 'utils/lms/userContextHelpers'
import { SITE_QUERY_PARAM } from './constants'

/**
 * Resolves the curriculum LMS ensure payload for HS page loads.
 * Prefers URL program config; falls back to HS training defaults.
 * Remaps legacy LMS codes (e.g. `highschool`) to curriculum-service
 * canonical codes (`9-12_core_tier-1_mixed`) so LMS can resolve authored structure.
 */
export const resolveHsCurriculumLmsPayload = ({
  siteId,
  pathname = typeof window !== 'undefined' ? window.location?.pathname : '',
} = {}) => {
  if (siteId == null) return null

  const programConfig = resolveTrainingProgramConfig(pathname)
  const contentGroupCode =
    programConfig?.contentGroupCode || HIGH_SCHOOL_PROGRAM_TYPE
  const rawCourseCode =
    programConfig?.trainingCourseCode || HS_TRAINING_COURSE_CODE

  return {
    contentGroupCode,
    courseCode:
      resolveCanonicalCourseCode(contentGroupCode, rawCourseCode) ||
      rawCourseCode,
    siteId,
  }
}

export const getDefaultSite = siteList => {
  if (siteList?.length === 0 || !Array.isArray(siteList)) {
    return null
  }

  const sortedSites = [...siteList].sort((a, b) => {
    const aName = a?.name || a?.siteName || ''
    const bName = b?.name || b?.siteName || ''
    return aName.localeCompare(bName)
  })

  const firstSite = sortedSites[0]

  const siteId = firstSite?.id || firstSite?.siteId || ''
  const siteName = firstSite?.name || firstSite?.siteName || ''

  return { siteId, siteName }
}

export const getSiteFromUrl = siteList => {
  // Handle test environment where window.location might be mocked
  try {
    const queryParams = new URLSearchParams(location?.search || '')
    const siteId = queryParams.get(SITE_QUERY_PARAM)
    return findSiteById(siteList, siteId)
  } catch (error) {
    // In case of errors in test environment, return null
    return null
  }
}

export const findSiteById = (siteList, searchId) => {
  const matchingSite = findUserContextSiteById(siteList, searchId)

  // If no matching site is found, return null
  if (!matchingSite) return null

  // Handle test environment where site objects might have different structure
  const siteId = matchingSite?.id || matchingSite?.siteId
  const siteName = matchingSite?.name || matchingSite?.siteName || ''
  if (!siteId) return null

  const isSetup =
    matchingSite?.isHSSetUp === undefined ? true : matchingSite?.isHSSetUp

  return {
    siteId,
    siteName,
    isSetup: !!isSetup,
    programPreferences: {
      highSchool: !!matchingSite?.isHSSetUp,
    },
  }
}

export const isSiteQueryParamPresent = () => {
  try {
    const queryParams = new URLSearchParams(location?.search || '')
    return queryParams.has(SITE_QUERY_PARAM)
  } catch (error) {
    // In case of errors in test environment, return false
    return false
  }
}

// Used by v3 curriculum pages too. Keep DWB first or program-supports loses siteId.
export const getProgramType = () => {
  try {
    if (isDigitalWellBeingUrl()) {
      return 'dwb'
    }
    if (isHighSchoolDashboard() || isHighSchoolUrl()) {
      return 'highSchool'
    }
    if (isMiddleSchoolDashboard() || isMiddleSchoolUrl()) {
      return 'middleSchool'
    }
    if (isK5Dashboard() || isK5Url()) {
      return 'k5'
    }

    return null
  } catch (error) {
    console.error('Error determining program type:', error)
    return null
  }
}

// Helper function to navigate to a new URL
export const navigate = url => {
  try {
    window.location.href = url.toString()
  } catch (error) {
    console.error('Navigation error:', error)
  }
}

// Helper function to get the active site ID key for the current program type
export const getActiveSiteIdKeyForProgramType = programType => {
  // DWB teach URLs resolve to k5/middleSchool/highSchool in productHelpers, so
  // pathname must win over programType. userContext stores digitalWellBeingActiveSiteId.
  if (isDigitalWellBeingPath(location?.pathname)) {
    return 'digitalWellBeingActiveSiteId'
  }

  switch (programType) {
    case 'highSchool':
      return 'hsActiveSiteId'
    case 'middleSchool':
      return 'middleSchoolActiveSiteId'
    case 'k5':
      return 'elementaryActiveSiteId'
    default:
      return null
  }
}

// Function to check if a site is set up
export const isSiteSetup = (site, programType, userContext) => {
  if (!site) return false

  // For Middle School and K5, we don't use the isSetup flag
  // Those programs are always considered "set up" if they exist
  if (
    programType === 'middleSchool' ||
    programType === 'k5' ||
    programType === 'dwb'
  ) {
    return true
  }

  // For High School, we need to check the isSetup flag
  if (programType === 'highSchool') {
    // First, check if the site already has programPreferences set
    if (site?.programPreferences?.highSchool) {
      return Boolean(site?.programPreferences?.highSchool)
    }

    // Fallback to checking the user context sites data
    if (!userContext?.sites?.length) return false

    const siteInContext = userContext?.sites?.find(
      s => s?.siteId === site?.siteId,
    )
    if (!siteInContext || !siteInContext?.programs?.highSchool) return false

    return Boolean(siteInContext?.programs?.highSchool?.preferences?.isSetup)
  }

  // If no specific program type is detected, assume the site is set up
  return true
}

// Function to get site and user preferences
export const getSiteAndUserPreferences = (site, allSites, programType) => {
  if (!site) return site

  // Handle case where allSites might be null or empty
  if (!allSites || !Array.isArray(allSites) || allSites.length === 0) {
    return site
  }

  // Find the site in allSites, handling both id and siteId
  const selectedSite = allSites?.find(
    s => site?.siteId === s?.id || site?.siteId === s?.siteId,
  )

  if (!selectedSite) {
    return site
  }

  const updatedSite = { ...site }
  updatedSite.claims = selectedSite?.claims || []

  updatedSite.preferences = selectedSite?.preferences || []

  switch (programType) {
    case 'highSchool':
      updatedSite.highSchoolEducatorPreferences = getUserProgramPreferencesFromArray(
        selectedSite?.preferences,
        {
          accessCode: ACCESS_CODES.HS,
        },
      )
      break
    case 'middleSchool':
      updatedSite.middleSchoolEducatorPreferences = getUserProgramPreferencesFromArray(
        selectedSite?.preferences,
        {
          accessCode: ACCESS_CODES.MSP,
        },
      )
      break
    case 'k5':
      updatedSite.k5EducatorPreferences = getUserProgramPreferencesFromArray(
        selectedSite?.preferences,
        { accessCode: ACCESS_CODES.K5 },
      )
      break
    default:
      break
  }

  return updatedSite
}
